import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiX,
  FiCloud,
  FiCheck,
  FiAlertCircle,
  FiRefreshCw,
  FiDownload,
  FiSearch,
  FiCheckSquare,
  FiSquare,
  FiArrowLeft,
  FiTag,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { listBedrockPrompts, importBedrockPrompt, autoConfigureBedrock, listEnvironments } from '../../../../../services/aiOperationsApi';
import BedrockErrorAlert from './BedrockErrorAlert';

const KNOWN_ENVS = ['development', 'staging', 'production'];

const extractEnvironment = (name: string): { baseName: string; environment: string | null } => {
  const lastDash = name.lastIndexOf('-');
  if (lastDash === -1 || lastDash === 0) {
    return { baseName: name, environment: null };
  }
  const suffix = name.slice(lastDash + 1);
  if (/^[a-z]{2,20}$/.test(suffix)) {
    return { baseName: name.slice(0, lastDash), environment: suffix };
  }
  return { baseName: name, environment: null };
};

interface EnvConfig {
  label: string;
  color: string;
}

const DEFAULT_ENV_CONFIG: Record<string, EnvConfig> = {
  development: { label: 'Dev', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  staging: { label: 'Staging', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  production: { label: 'Prod', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
};

const getEnvConfig = (env: string | null): EnvConfig => {
  if (!env) return { label: 'Unassigned', color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' };
  if (DEFAULT_ENV_CONFIG[env]) return DEFAULT_ENV_CONFIG[env];
  return {
    label: env.charAt(0).toUpperCase() + env.slice(1),
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  };
};

interface BedrockPrompt {
  id: string;
  name: string;
  baseName: string;
  detectedEnv: string | null;
  description?: string;
  version?: string;
  is_imported?: boolean;
}

interface ImportSuccess {
  bedrockId: string;
  name: string;
  prompt: unknown;
}

interface ImportFailure {
  bedrockId: string;
  name: string;
  error: string;
}

interface ImportResults {
  success: ImportSuccess[];
  failed: ImportFailure[];
}

interface FilterPill {
  key: string;
  label: string;
  count: number;
}

type ModalStep = 'browse' | 'assign';

interface ImportFromBedrockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: (results: ImportSuccess[]) => void;
}

const ImportFromBedrockModal: React.FC<ImportFromBedrockModalProps> = ({ isOpen, onClose, onImportComplete }) => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<BedrockPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [envFilter, setEnvFilter] = useState('all');
  const [step, setStep] = useState<ModalStep>('browse');
  const [envAssignments, setEnvAssignments] = useState<Record<string, string>>({});
  const [knownEnvironments, setKnownEnvironments] = useState<string[]>([...KNOWN_ENVS]);

  const fetchPrompts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      try { await autoConfigureBedrock(); } catch (_) { console.error('Bedrock auto-configure failed (non-critical)', _); }

      const [response, envResponse] = await Promise.all([
        listBedrockPrompts(false),
        listEnvironments().catch(() => ({ environments: [...KNOWN_ENVS] })),
      ]);

      const allPrompts = response.data ?? response.prompts ?? [];

      if (envResponse.environments) {
        setKnownEnvironments(prev => {
          const merged = new Set(Array.from(prev).concat(Array.from(envResponse.environments)));
          return Array.from(merged).sort((a, b) => a.localeCompare(b));
        });
      }

      const enriched: BedrockPrompt[] = allPrompts
        .filter((p: { is_imported?: boolean }) => !p.is_imported)
        .map((p: Record<string, unknown>) => {
          const { baseName, environment } = extractEnvironment(p.name as string);
          return { ...p, baseName, detectedEnv: environment } as BedrockPrompt;
        });

      setPrompts(enriched);
    } catch (err: unknown) {
      console.error('Failed to fetch Bedrock prompts:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchPrompts();
      setSelected([]);
      setImportResults(null);
      setSearchTerm('');
      setEnvFilter('all');
      setStep('browse');
      setEnvAssignments({});
    }
  }, [isOpen, fetchPrompts]);

  const detectedEnvs = useMemo(() => {
    const envs = new Set<string>();
    prompts.forEach(p => { if (p.detectedEnv) envs.add(p.detectedEnv); });
    return Array.from(envs).sort((a, b) => a.localeCompare(b));
  }, [prompts]);

  const envCounts = useMemo(() => {
    const counts: Record<string, number> = { all: prompts.length };
    prompts.forEach(p => {
      const key = p.detectedEnv ?? '_unassigned';
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [prompts]);

  const filterPills = useMemo((): FilterPill[] => {
    const pills: FilterPill[] = [{ key: 'all', label: 'All', count: envCounts.all }];
    detectedEnvs.forEach(env => {
      const config = getEnvConfig(env);
      pills.push({ key: env, label: config.label, count: envCounts[env] ?? 0 });
    });
    if ((envCounts._unassigned ?? 0) > 0) {
      pills.push({ key: '_unassigned', label: 'Unassigned', count: envCounts._unassigned });
    }
    return pills;
  }, [detectedEnvs, envCounts]);

  const filteredPrompts = useMemo(() => {
    return prompts.filter(prompt => {
      const matchesSearch = !searchTerm ||
        prompt.baseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        prompt.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesEnv = envFilter === 'all' ||
        (envFilter === '_unassigned' ? !prompt.detectedEnv : prompt.detectedEnv === envFilter);
      return matchesSearch && matchesEnv;
    });
  }, [prompts, searchTerm, envFilter]);

  const toggleSelect = (promptId: string) => {
    setSelected(prev =>
      prev.includes(promptId)
        ? prev.filter(id => id !== promptId)
        : [...prev, promptId]
    );
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredPrompts.map(p => p.id);
    const allSelected = visibleIds.every(id => selected.includes(id));
    if (allSelected) {
      setSelected(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelected(prev => Array.from(new Set(Array.from(prev).concat(Array.from(visibleIds)))));
    }
  };

  const handleImportClick = () => {
    const needsAssignment = selected.some(id => {
      const prompt = prompts.find(p => p.id === id);
      return prompt && !prompt.detectedEnv;
    });

    if (needsAssignment) {
      const assignments: Record<string, string> = {};
      selected.forEach(id => {
        const prompt = prompts.find(p => p.id === id);
        if (prompt && !prompt.detectedEnv) {
          assignments[id] = envAssignments[id] ?? 'development';
        }
      });
      setEnvAssignments(prev => ({ ...prev, ...assignments }));
      setStep('assign');
    } else {
      doImport();
    }
  };

  const doImport = async () => {
    if (selected.length === 0) return;

    setImporting(true);
    setImportResults(null);

    const results: ImportResults = { success: [], failed: [] };

    for (const bedrockId of selected) {
      try {
        const prompt = prompts.find(p => p.id === bedrockId);
        const env = prompt?.detectedEnv ?? envAssignments[bedrockId] ?? 'development';
        const result = await importBedrockPrompt(bedrockId, { environment: env, labels: [] });
        results.success.push({ bedrockId, name: prompt?.baseName ?? bedrockId, prompt: result.prompt });
      } catch (err: unknown) {
        const prompt = prompts.find(p => p.id === bedrockId);
        results.failed.push({
          bedrockId,
          name: prompt?.baseName ?? bedrockId,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    setImportResults(results);
    setImporting(false);
    setStep('browse');

    setPrompts(prev => prev.filter(p => !results.success.some(s => s.bedrockId === p.id)));
    setSelected([]);
    setEnvAssignments({});

    if (onImportComplete && results.success.length > 0) {
      onImportComplete(results.success);
    }
  };

  if (!isOpen) return null;

  const allVisibleSelected = filteredPrompts.length > 0 && filteredPrompts.every(p => selected.includes(p.id));

  const envNames = Array.from(new Set(prompts.map(p => p.detectedEnv).filter(Boolean)));
  let summaryText: string;
  if (loading) {
    summaryText = 'Connecting to AWS Bedrock...';
  } else if (prompts.length === 0) {
    summaryText = 'No untracked prompts found';
  } else {
    summaryText = `${prompts.length} prompt${prompts.length === 1 ? '' : 's'} across ${envNames.length} environment${envNames.length === 1 ? '' : 's'}`;
  }

  const promptsToAssign = step === 'assign'
    ? selected.map(id => prompts.find(p => p.id === id)).filter((p): p is BedrockPrompt => !!p && !p.detectedEnv)
    : [];

  const promptSuffix = selected.length === 1 ? '' : 's';
  const importButtonLabel = importing
    ? `Importing ${selected.length}...`
    : `Import ${selected.length} Prompt${promptSuffix}`;

  const assignSuffix = promptsToAssign.length === 1 ? '' : 's';
  const assignHeaderText = step === 'assign'
    ? `${promptsToAssign.length} prompt${assignSuffix} need an environment`
    : summaryText;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }} role="presentation" />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                {step === 'assign' ? (
                  <FiTag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                ) : (
                  <FiCloud className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {step === 'assign' ? 'Assign Environments' : 'Import from AWS Bedrock'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {assignHeaderText}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-140px)]">
            {step === 'assign' ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  These prompts don&apos;t follow the <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">name-environment</code> convention.
                  Please assign an environment to each one before importing.
                </p>

                <div className="space-y-3">
                  {promptsToAssign.map((prompt) => (
                    <div key={prompt.id} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {prompt.baseName}
                        </h4>
                        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate">
                          {prompt.id}
                        </p>
                      </div>
                      <select
                        value={envAssignments[prompt.id] ?? 'development'}
                        onChange={(e) => setEnvAssignments(prev => ({ ...prev, [prompt.id]: e.target.value }))}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {knownEnvironments.map(env => (
                          <option key={env} value={env}>
                            {env.charAt(0).toUpperCase() + env.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {!loading && prompts.length > 0 && (
                  <div className="mb-4 space-y-3">
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by prompt name..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {filterPills.length > 2 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {filterPills.map(pill => {
                          const isActive = envFilter === pill.key;
                          return (
                            <button
                              key={pill.key}
                              onClick={() => setEnvFilter(pill.key)}
                              className={`
                                inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors
                                ${isActive
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }
                              `}
                            >
                              {pill.label}
                              <span className={`
                                ${isActive ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'}
                                text-[10px] px-1.5 py-0.5 rounded-full min-w-[20px] text-center
                              `}>
                                {pill.count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {error && (
                  <>
                    <BedrockErrorAlert error={error} />
                    {error.toLowerCase().includes('credentials') && (
                      <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-center">
                        <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                          Make sure you have an AWS account connected in your platform settings.
                        </p>
                        <button
                          onClick={() => { onClose(); navigate('/hub'); }}
                          className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Go to Hub Settings
                        </button>
                      </div>
                    )}
                  </>
                )}

                {importResults && (
                  <div className="mb-4 space-y-2">
                    {importResults.success.length > 0 && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                          <FiCheck className="w-4 h-4 flex-shrink-0" />
                          <span>
                            Imported {importResults.success.length} prompt{importResults.success.length === 1 ? '' : 's'}:
                            {' '}{importResults.success.map(s => s.name).join(', ')}
                          </span>
                        </div>
                      </div>
                    )}
                    {importResults.failed.length > 0 && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                          <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span>
                            Failed to import {importResults.failed.length} prompt{importResults.failed.length === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-16">
                    <FiRefreshCw className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Connecting to AWS Bedrock...
                    </p>
                  </div>
                )}
                {!loading && filteredPrompts.length === 0 && (
                  <div className="text-center py-16">
                    <FiCloud className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400 font-medium">
                      {searchTerm || envFilter !== 'all'
                        ? 'No prompts match your filters'
                        : 'No untracked prompts found in Bedrock'}
                    </p>
                    {!searchTerm && envFilter === 'all' && (
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                        All Bedrock prompts are already tracked in Aivilo.
                      </p>
                    )}
                  </div>
                )}
                {!loading && filteredPrompts.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                      >
                        {allVisibleSelected ? (
                          <FiCheckSquare className="w-4 h-4 text-blue-500" />
                        ) : (
                          <FiSquare className="w-4 h-4" />
                        )}
                        Select all ({filteredPrompts.length})
                      </button>
                      {selected.length > 0 && (
                        <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                          {selected.length} selected
                        </span>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      {filteredPrompts.map((prompt) => {
                        const isSelected = selected.includes(prompt.id);
                        const envConfig = getEnvConfig(prompt.detectedEnv);
                        return (
                          <div
                            key={prompt.id}
                            onClick={() => toggleSelect(prompt.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSelect(prompt.id); }} role="button" tabIndex={0}
                            className={`
                              flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer
                              border transition-all duration-150
                              ${isSelected
                                ? 'border-blue-300 dark:border-blue-600 bg-blue-50/50 dark:bg-blue-900/20 shadow-sm'
                                : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }
                            `}
                          >
                            <div className="flex-shrink-0">
                              {isSelected ? (
                                <FiCheckSquare className="w-4 h-4 text-blue-500" />
                              ) : (
                                <FiSquare className="w-4 h-4 text-gray-400" />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {prompt.baseName}
                                </h4>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${envConfig.color}`}>
                                  {envConfig.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                {prompt.description ? (
                                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {prompt.description}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate">
                                    {prompt.id}
                                  </span>
                                )}
                              </div>
                            </div>

                            {prompt.version && (
                              <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                                {prompt.version}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            {step === 'assign' ? (
              <>
                <button
                  onClick={() => setStep('browse')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400
                           hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <FiArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  onClick={doImport}
                  disabled={importing}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium
                           bg-blue-600 text-white rounded-lg
                           hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FiDownload className="w-4 h-4" />
                  {importButtonLabel}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={fetchPrompts}
                  disabled={loading}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400
                           hover:text-gray-900 dark:hover:text-white disabled:opacity-50 transition-colors"
                >
                  <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>

                <div className="flex items-center gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                             hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImportClick}
                    disabled={selected.length === 0 || importing}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium
                             bg-blue-600 text-white rounded-lg
                             hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FiDownload className="w-4 h-4" />
                    {importButtonLabel}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportFromBedrockModal;
